export interface RouteMetadata {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  keywords?: string;
  canonicalUrl?: string;
}

export const routeMetadataConfig: Record<string, RouteMetadata> = {
  '/': {
    title: 'Growth OS - AI-Powered Growth Platform',
    description: 'AI-powered growth and optimization platform for agencies. Streamline your growth strategies with advanced analytics and automation.',
    ogTitle: 'Growth OS - AI Growth Platform',
    ogDescription: 'Optimize your agency\'s growth with AI-powered analysis, SEO tools, and growth hacking strategies',
    keywords: 'growth agency, AI, optimization, SEO, growth hacking, agency tools',
  },
  '/dashboard': {
    title: 'Dashboard - Growth OS',
    description: 'View your growth metrics, analytics, and performance overview in one place.',
    keywords: 'dashboard, analytics, metrics, performance',
  },
  '/sop-dashboard': {
    title: 'SOP Dashboard - Growth OS',
    description: 'Manage your Standard Operating Procedures, track progress, and optimize your workflows.',
    ogImage: '/lovable-uploads/sop-dashboard-og.png', // Add your specific OG image URL here
    keywords: 'SOP, standard operating procedures, workflow, process management',
  },
  '/playbook-progress': {
    title: 'Growth Playbook Progress - Growth OS',
    description: 'Track your growth playbook progress, monitor task completion, and achieve your growth goals.',
    keywords: 'growth playbook, progress tracking, task management, goals',
  },
  '/content-generation': {
    title: 'Content Generation - Growth OS',
    description: 'Generate high-quality content with AI-powered tools for your marketing campaigns.',
    keywords: 'content generation, AI content, marketing content, copywriting',
  },
  '/visual-generation': {
    title: 'Visual Generation - Growth OS',
    description: 'Create stunning visuals and graphics with AI-powered image generation tools.',
    keywords: 'visual generation, AI images, graphics, design tools',
  },
  '/seo-analyzer': {
    title: 'SEO Analyzer - Growth OS',
    description: 'Analyze and optimize your website\'s SEO performance with advanced AI-powered tools.',
    keywords: 'SEO analyzer, search optimization, SEO tools, website analysis',
  },
  '/competitor-analysis': {
    title: 'Competitor Analysis - Growth OS',
    description: 'Gain insights into your competitors\' strategies and find opportunities to outperform them.',
    keywords: 'competitor analysis, market research, competitive intelligence',
  },
  '/settings': {
    title: 'Settings - Growth OS',
    description: 'Manage your account settings, preferences, and configurations.',
    keywords: 'settings, account, preferences, configuration',
  },
  '/settings/profile': {
    title: 'Profile Settings - Growth OS',
    description: 'Update your profile information and personal preferences.',
    keywords: 'profile, user settings, personal information',
  },
  '/settings/metadata': {
    title: 'Metadata Settings - Growth OS',
    description: 'Configure your site\'s metadata, OG tags, and SEO settings.',
    keywords: 'metadata, SEO settings, OG tags, site configuration',
  },
  '/onboarding': {
    title: 'Welcome to Growth OS',
    description: 'Get started with Growth OS and set up your growth strategy.',
    keywords: 'onboarding, getting started, setup, welcome',
  },
  '/auth': {
    title: 'Sign In - Growth OS',
    description: 'Sign in to your Growth OS account to access your growth tools.',
    keywords: 'login, sign in, authentication',
  },
  '/trial-expired': {
    title: 'Trial Expired - Growth OS',
    description: 'Your trial has expired. Upgrade now to continue using Growth OS.',
    keywords: 'trial expired, upgrade, subscription',
  },
  '/admin/llm-config': {
    title: 'LLM Source Configuration - Growth OS',
    description: 'Configure which LLM provider and model to use for each feature or use case. Map AI capabilities to specific providers like OpenAI, Claude, Gemini, and Perplexity.',
    keywords: 'LLM configuration, AI providers, model selection, OpenAI, Claude, Gemini, Perplexity, AI settings',
  },
  '/human-resources/work-calendar': {
    title: 'Employee Work Calendar - Growth OS',
    description: 'View daily working hours for each employee against an 8-hour workday benchmark. Track time and vacation data.',
    keywords: 'work calendar, employee hours, time tracking, vacation, HR, attendance',
  },
};

// Helper function to get metadata for a route
export const getMetadataForRoute = (pathname: string): RouteMetadata | null => {
  // Exact match first
  if (routeMetadataConfig[pathname]) {
    return routeMetadataConfig[pathname];
  }

  // Try to match parent routes (e.g., /settings/profile -> /settings)
  const pathSegments = pathname.split('/').filter(Boolean);
  for (let i = pathSegments.length; i > 0; i--) {
    const parentPath = '/' + pathSegments.slice(0, i).join('/');
    if (routeMetadataConfig[parentPath]) {
      return routeMetadataConfig[parentPath];
    }
  }

  return null;
};
